import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { StewieLogo } from '../components/StewieLogo';
import {
  ArrowLeft, RefreshCw, AlertTriangle, CheckCircle, Eye,
  Clock, FileText, Search, ChevronDown, X, MessageSquare,
  BookOpen, Flag, Archive,
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  flagged: { label: 'Flagged', color: '#ef4444', bg: '#ef444418', icon: Flag },
  reviewed: { label: 'Reviewed', color: '#f59e0b', bg: '#f59e0b18', icon: Eye },
  resolved: { label: 'Resolved', color: '#10b981', bg: '#10b98118', icon: CheckCircle },
  not_needed: { label: 'OK', color: '#64748b', bg: '#64748b18', icon: CheckCircle },
};

const CONF_STYLES = {
  High: { color: '#10b981', bg: '#10b98118' },
  Medium: { color: '#f59e0b', bg: '#f59e0b18' },
  Low: { color: '#ef4444', bg: '#ef444418' },
};

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' +
      d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

export default function ReviewQueuePage({ user }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('flagged');
  const [total, setTotal] = useState(0);
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [suggestedAnswer, setSuggestedAnswer] = useState('');
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    const authHeaders = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    try {
      const [itemsRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/rag/review-queue?status=${filterStatus}&limit=50`, { headers: authHeaders }),
        axios.get(`${API}/api/rag/review-queue/stats`, { headers: authHeaders }),
      ]);
      setItems(itemsRes.data.items || []);
      setTotal(itemsRes.data.total || 0);
      setStats(statsRes.data);
    } catch (err) { console.error('Failed to fetch review queue:', err); }
    finally { setLoading(false); }
  }, [filterStatus]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openDetail = async (item) => {
    setDetailLoading(true);
    setSelectedItem(item);
    setAdminNotes(item.admin_notes || '');
    setSuggestedAnswer(item.suggested_answer || '');
    try {
      const res = await axios.get(`${API}/api/rag/review-queue/item/${item.id}`, { headers });
      setSelectedItem(res.data);
      setSuggestedAnswer(res.data.suggested_answer || '');
    } catch (err) { console.error(err); }
    finally { setDetailLoading(false); }
  };

  const updateStatus = async (logId, newStatus) => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/rag/review-queue/${logId}`, {
        review_status: newStatus,
        admin_notes: adminNotes,
        suggested_answer: suggestedAnswer || null,
      }, { headers });
      setSelectedItem(prev => prev ? { ...prev, review_status: newStatus } : null);
      await fetchData();
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const sc = stats?.status_counts || {};

  return (
    <div className="rq-dash" data-testid="review-queue-page">
      <style>{rqStyles}</style>

      <header className="rq-header">
        <div className="rq-header-left">
          <div>
            <h1 className="rq-title">Review Queue</h1>
            <p className="rq-subtitle">Low-confidence answers flagged for admin review</p>
          </div>
        </div>
        <button className="rq-refresh" onClick={fetchData} disabled={loading} data-testid="rq-refresh-btn">
          <RefreshCw size={16} className={loading ? 'rq-spin' : ''} /> Refresh
        </button>
      </header>

      {/* Stats */}
      <div className="rq-stats-grid" data-testid="rq-stat-cards">
        <StatCard icon={Flag} label="Flagged" value={sc.flagged || 0} color="#ef4444" />
        <StatCard icon={Eye} label="Reviewed" value={sc.reviewed || 0} color="#f59e0b" />
        <StatCard icon={CheckCircle} label="Resolved" value={sc.resolved || 0} color="#10b981" />
        <StatCard icon={MessageSquare} label="Total Logs" value={Object.values(sc).reduce((a, b) => a + b, 0)} color="#3b82f6" />
      </div>

      {/* Weak Docs */}
      {stats?.weak_docs?.length > 0 && (
        <div className="rq-weak-docs" data-testid="rq-weak-docs">
          <h3 className="rq-section-title"><AlertTriangle size={14} /> Documents Appearing in Flagged Answers</h3>
          <div className="rq-weak-list">
            {stats.weak_docs.map((d, i) => (
              <span key={i} className="rq-weak-pill">{d.title} ({d.flag_count})</span>
            ))}
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="rq-filters" data-testid="rq-filters">
        {['flagged', 'reviewed', 'resolved', 'not_needed', 'all'].map(s => (
          <button key={s} className={`rq-filter-btn ${filterStatus === s ? 'active' : ''}`}
            onClick={() => setFilterStatus(s)} data-testid={`rq-filter-${s}`}>
            {s === 'all' ? 'All' : (STATUS_STYLES[s]?.label || s)} {s !== 'all' && `(${sc[s] || 0})`}
          </button>
        ))}
        <span className="rq-count">{total} items</span>
      </div>

      {/* Items List */}
      <div className="rq-items" data-testid="rq-items-list">
        {items.length === 0 && !loading && (
          <div className="rq-empty">No items match the current filter.</div>
        )}
        {items.map((item) => {
          const sInfo = STATUS_STYLES[item.review_status] || STATUS_STYLES.flagged;
          const cInfo = CONF_STYLES[item.confidence] || CONF_STYLES.Low;
          return (
            <div key={item.id} className="rq-item" onClick={() => openDetail(item)}
              data-testid={`rq-item-${item.id}`}>
              <div className="rq-item-left">
                <div className="rq-item-question">{item.question}</div>
                <div className="rq-item-meta">
                  <span className="rq-badge" style={{ background: sInfo.bg, color: sInfo.color }}>
                    {sInfo.label}
                  </span>
                  <span className="rq-badge" style={{ background: cInfo.bg, color: cInfo.color }}>
                    {item.confidence}
                  </span>
                  {item.flag_reason && (
                    <span className="rq-flag-reason">{item.flag_reason.replace(/_/g, ' ')}</span>
                  )}
                  {item.intent && (
                    <span className="rq-badge" style={{ background: '#3b82f618', color: '#3b82f6' }}>{item.intent.replace(/_/g, ' ')}</span>
                  )}
                  {item.answer_type && (
                    <span className="rq-badge" style={{ background: '#8b5cf618', color: '#8b5cf6' }}>{item.answer_type.replace(/_/g, ' ')}</span>
                  )}
                  <span className="rq-mode-badge">{item.mode}</span>
                </div>
              </div>
              <div className="rq-item-right">
                <div className="rq-item-scores">
                  <span>Top: {(item.retrieval_score_top || 0).toFixed(3)}</span>
                  <span>Avg: {(item.retrieval_score_avg || 0).toFixed(3)}</span>
                </div>
                <div className="rq-item-time">{fmtDate(item.created_at)}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Detail Panel */}
      {selectedItem && (
        <div className="rq-overlay" onClick={() => setSelectedItem(null)} data-testid="rq-detail-modal">
          <div className="rq-detail" onClick={e => e.stopPropagation()}>
            <div className="rq-detail-header">
              <h3>Review Detail</h3>
              <button className="rq-close" onClick={() => setSelectedItem(null)}><X size={18} /></button>
            </div>
            <div className="rq-detail-body">
              <div className="rq-detail-section">
                <label>Question</label>
                <div className="rq-detail-text">{selectedItem.question}</div>
              </div>
              <div className="rq-detail-section">
                <label>Answer</label>
                <div className="rq-detail-text rq-detail-answer">{selectedItem.answer}</div>
              </div>
              <div className="rq-detail-row">
                <div><label>Confidence</label><span className="rq-badge" style={{
                  background: (CONF_STYLES[selectedItem.confidence] || {}).bg,
                  color: (CONF_STYLES[selectedItem.confidence] || {}).color,
                }}>{selectedItem.confidence}</span></div>
                <div><label>Mode</label><span>{selectedItem.mode}</span></div>
                <div><label>Top Score</label><span>{(selectedItem.retrieval_score_top || 0).toFixed(4)}</span></div>
                <div><label>Avg Score</label><span>{(selectedItem.retrieval_score_avg || 0).toFixed(4)}</span></div>
                <div><label>Flag Reason</label><span>{selectedItem.flag_reason || '—'}</span></div>
              </div>

              {/* Intent & Answer Type */}
              <div className="rq-detail-row" data-testid="rq-intent-row">
                <div><label>Intent</label><span className="rq-badge" style={{ background: '#3b82f618', color: '#3b82f6' }}>{selectedItem.intent || '—'}</span></div>
                <div><label>Answer Type</label><span className="rq-badge" style={{ background: '#8b5cf618', color: '#8b5cf6' }}>{(selectedItem.answer_type || '—').replace(/_/g, ' ')}</span></div>
                <div><label>Citations</label><span>{selectedItem.citation_count || 0}</span></div>
                <div><label>Created</label><span style={{ fontSize: 12 }}>{fmtDate(selectedItem.created_at)}</span></div>
              </div>

              {/* Semantic Trace (if available from the log) */}
              {(selectedItem.used_chunk_ids?.length > 0 || selectedItem.used_doc_ids?.length > 0) && (
                <div className="rq-detail-section" data-testid="rq-semantic-trace">
                  <label>Retrieval Trace</label>
                  <div style={{ fontSize: 12, color: '#94a3b8', background: '#0f172a', borderRadius: 8, padding: '8px 12px', border: '1px solid #1e293b' }}>
                    {selectedItem.used_doc_ids?.length > 0 && (
                      <div style={{ marginBottom: 4 }}>Docs used: {selectedItem.used_doc_ids.length}</div>
                    )}
                    {selectedItem.used_chunk_ids?.length > 0 && (
                      <div>Chunks used: {selectedItem.used_chunk_ids.length} ({selectedItem.used_chunk_ids.slice(0, 5).join(', ')}{selectedItem.used_chunk_ids.length > 5 ? '...' : ''})</div>
                    )}
                  </div>
                </div>
              )}

              {/* Referenced Docs */}
              {selectedItem.referenced_docs?.length > 0 && (
                <div className="rq-detail-section">
                  <label><BookOpen size={13} /> Referenced Documents</label>
                  <div className="rq-ref-list">
                    {selectedItem.referenced_docs.map((d, i) => (
                      <span key={i} className="rq-ref-pill">
                        {d.title} <span className="rq-ref-type">({d.doc_type})</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Referenced Chunks */}
              {selectedItem.referenced_chunks?.length > 0 && (
                <div className="rq-detail-section">
                  <label><FileText size={13} /> Source Chunks Used ({selectedItem.referenced_chunks.length})</label>
                  <div className="rq-chunks-list">
                    {selectedItem.referenced_chunks.slice(0, 5).map((c, i) => (
                      <div key={i} className="rq-chunk-card" data-testid={`rq-chunk-${i}`}>
                        <div className="rq-chunk-meta">Chunk #{c.chunk_index} &middot; Page ~{c.approx_page}</div>
                        <div className="rq-chunk-text">{(c.content || '').slice(0, 300)}{(c.content || '').length > 300 ? '...' : ''}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Admin Notes */}
              <div className="rq-detail-section">
                <label>Admin Notes</label>
                <textarea className="rq-notes" value={adminNotes} onChange={e => setAdminNotes(e.target.value)}
                  placeholder="Add review notes..." rows={3} data-testid="rq-admin-notes" />
              </div>

              {/* Suggested Correct Answer */}
              <div className="rq-detail-section" data-testid="rq-suggested-answer-section">
                <label>Suggested Correct Answer</label>
                <textarea className="rq-notes" value={suggestedAnswer} onChange={e => setSuggestedAnswer(e.target.value)}
                  placeholder="If you know the correct answer, write it here to improve future responses..."
                  rows={4} data-testid="rq-suggested-answer" />
              </div>

              {/* Actions */}
              <div className="rq-actions" data-testid="rq-actions">
                {selectedItem.review_status !== 'reviewed' && (
                  <button className="rq-action-btn rq-action-review" disabled={saving}
                    onClick={() => updateStatus(selectedItem.id, 'reviewed')} data-testid="rq-mark-reviewed">
                    <Eye size={14} /> Mark Reviewed
                  </button>
                )}
                {selectedItem.review_status !== 'resolved' && (
                  <button className="rq-action-btn rq-action-resolve" disabled={saving}
                    onClick={() => updateStatus(selectedItem.id, 'resolved')} data-testid="rq-mark-resolved">
                    <CheckCircle size={14} /> Resolve
                  </button>
                )}
                {selectedItem.review_status !== 'flagged' && (
                  <button className="rq-action-btn rq-action-flag" disabled={saving}
                    onClick={() => updateStatus(selectedItem.id, 'flagged')} data-testid="rq-re-flag">
                    <Flag size={14} /> Re-Flag
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="rq-stat-card" data-testid={`rq-stat-${label.toLowerCase()}`}>
      <div className="rq-stat-icon" style={{ background: `${color}18`, color }}><Icon size={20} /></div>
      <div className="rq-stat-info">
        <span className="rq-stat-value">{value}</span>
        <span className="rq-stat-label">{label}</span>
      </div>
    </div>
  );
}

const rqStyles = `
  .rq-dash {
    min-height: 100vh; background: #0b1120; color: #e2e8f0;
    padding: 24px 32px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }
  .rq-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 24px; flex-wrap: wrap; gap: 16px; }
  .rq-header-left { display: flex; align-items: center; gap: 12px; }
  .rq-back { display: flex; align-items: center; gap: 6px; background: transparent; color: #94a3b8; border: 1px solid #334155; padding: 8px 14px; border-radius: 8px; cursor: pointer; font-size: 13px; }
  .rq-back:hover { color: #e2e8f0; border-color: #475569; }
  .rq-title { font-size: 22px; font-weight: 700; color: #f8fafc; margin: 0; }
  .rq-subtitle { font-size: 13px; color: #64748b; margin: 2px 0 0; }
  .rq-refresh { display: flex; align-items: center; gap: 6px; background: #3b82f6; color: white; border: none; padding: 8px 16px; border-radius: 8px; cursor: pointer; font-size: 13px; font-weight: 500; }
  .rq-refresh:hover { background: #2563eb; }
  .rq-refresh:disabled { opacity: 0.6; }
  .rq-spin { animation: rq-spin 1s linear infinite; }
  @keyframes rq-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

  .rq-stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 12px; margin-bottom: 20px; }
  .rq-stat-card { background: #111827; border: 1px solid #1e293b; border-radius: 12px; padding: 16px; display: flex; align-items: center; gap: 12px; }
  .rq-stat-icon { width: 38px; height: 38px; border-radius: 10px; display: flex; align-items: center; justify-content: center; }
  .rq-stat-info { display: flex; flex-direction: column; }
  .rq-stat-value { font-size: 20px; font-weight: 700; color: #f8fafc; }
  .rq-stat-label { font-size: 10px; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; }

  .rq-weak-docs { background: #7f1d1d15; border: 1px solid #991b1b40; border-radius: 10px; padding: 14px 18px; margin-bottom: 18px; }
  .rq-section-title { font-size: 13px; font-weight: 600; color: #fca5a5; margin: 0 0 8px; display: flex; align-items: center; gap: 6px; }
  .rq-weak-list { display: flex; flex-wrap: wrap; gap: 6px; }
  .rq-weak-pill { background: #ef444418; color: #fca5a5; padding: 3px 10px; border-radius: 6px; font-size: 12px; font-weight: 500; }

  .rq-filters { display: flex; align-items: center; gap: 6px; margin-bottom: 16px; flex-wrap: wrap; }
  .rq-filter-btn { padding: 7px 14px; border-radius: 8px; border: 1px solid #1e293b; background: #111827; color: #64748b; cursor: pointer; font-size: 12px; font-weight: 500; }
  .rq-filter-btn:hover { color: #94a3b8; border-color: #334155; }
  .rq-filter-btn.active { background: #3b82f6; color: white; border-color: #3b82f6; }
  .rq-count { font-size: 12px; color: #64748b; margin-left: auto; }

  .rq-items { display: flex; flex-direction: column; gap: 8px; }
  .rq-empty { text-align: center; color: #475569; padding: 40px; font-size: 14px; }
  .rq-item { display: flex; justify-content: space-between; align-items: flex-start; background: #111827; border: 1px solid #1e293b; border-radius: 10px; padding: 14px 18px; cursor: pointer; transition: border-color 0.15s; }
  .rq-item:hover { border-color: #334155; }
  .rq-item-left { flex: 1; min-width: 0; }
  .rq-item-question { font-size: 14px; font-weight: 600; color: #f8fafc; margin-bottom: 6px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .rq-item-meta { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
  .rq-badge { display: inline-flex; align-items: center; gap: 3px; padding: 2px 8px; border-radius: 5px; font-size: 11px; font-weight: 600; }
  .rq-flag-reason { font-size: 11px; color: #94a3b8; background: #1e293b; padding: 2px 8px; border-radius: 4px; }
  .rq-mode-badge { font-size: 11px; color: #6366f1; background: #6366f118; padding: 2px 8px; border-radius: 4px; text-transform: capitalize; }
  .rq-item-right { display: flex; flex-direction: column; align-items: flex-end; gap: 4px; margin-left: 16px; flex-shrink: 0; }
  .rq-item-scores { display: flex; gap: 10px; font-size: 11px; color: #64748b; }
  .rq-item-time { font-size: 11px; color: #475569; }

  /* Detail Modal */
  .rq-overlay { position: fixed; inset: 0; background: #00000080; z-index: 100; display: flex; justify-content: flex-end; }
  .rq-detail { width: 600px; max-width: 90vw; background: #111827; border-left: 1px solid #1e293b; height: 100vh; overflow-y: auto; }
  .rq-detail-header { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; border-bottom: 1px solid #1e293b; position: sticky; top: 0; background: #111827; z-index: 1; }
  .rq-detail-header h3 { margin: 0; font-size: 16px; color: #f8fafc; }
  .rq-close { background: transparent; border: none; color: #64748b; cursor: pointer; }
  .rq-close:hover { color: #e2e8f0; }
  .rq-detail-body { padding: 20px; }
  .rq-detail-section { margin-bottom: 18px; }
  .rq-detail-section label { display: flex; align-items: center; gap: 5px; font-size: 11px; text-transform: uppercase; color: #64748b; letter-spacing: 0.5px; font-weight: 600; margin-bottom: 6px; }
  .rq-detail-text { font-size: 14px; color: #e2e8f0; line-height: 1.6; }
  .rq-detail-answer { background: #0f172a; padding: 12px; border-radius: 8px; border: 1px solid #1e293b; max-height: 200px; overflow-y: auto; font-size: 13px; }
  .rq-detail-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); gap: 12px; margin-bottom: 18px; }
  .rq-detail-row > div { display: flex; flex-direction: column; gap: 4px; }
  .rq-detail-row label { font-size: 10px; text-transform: uppercase; color: #475569; letter-spacing: 0.5px; }
  .rq-detail-row span { font-size: 13px; color: #e2e8f0; }

  .rq-ref-list { display: flex; flex-wrap: wrap; gap: 6px; }
  .rq-ref-pill { background: #1e293b; color: #94a3b8; padding: 4px 10px; border-radius: 6px; font-size: 12px; }
  .rq-ref-type { color: #475569; font-size: 10px; }

  .rq-chunks-list { display: flex; flex-direction: column; gap: 8px; }
  .rq-chunk-card { background: #0f172a; border: 1px solid #1e293b; border-radius: 8px; padding: 10px 12px; }
  .rq-chunk-meta { font-size: 10px; color: #64748b; margin-bottom: 4px; font-weight: 600; }
  .rq-chunk-text { font-size: 12px; color: #94a3b8; line-height: 1.5; }

  .rq-notes { width: 100%; background: #0f172a; border: 1px solid #1e293b; border-radius: 8px; padding: 10px; color: #e2e8f0; font-size: 13px; resize: vertical; outline: none; font-family: inherit; }
  .rq-notes:focus { border-color: #3b82f6; }
  .rq-notes::placeholder { color: #475569; }

  .rq-actions { display: flex; gap: 8px; flex-wrap: wrap; }
  .rq-action-btn { display: flex; align-items: center; gap: 6px; padding: 8px 16px; border-radius: 8px; border: none; font-size: 13px; font-weight: 600; cursor: pointer; }
  .rq-action-btn:disabled { opacity: 0.5; }
  .rq-action-review { background: #f59e0b20; color: #fcd34d; }
  .rq-action-review:hover { background: #f59e0b30; }
  .rq-action-resolve { background: #10b98120; color: #6ee7b7; }
  .rq-action-resolve:hover { background: #10b98130; }
  .rq-action-flag { background: #ef444420; color: #fca5a5; }
  .rq-action-flag:hover { background: #ef444430; }

  @media (max-width: 768px) {
    .rq-dash { padding: 16px; }
    .rq-header { flex-direction: column; }
    .rq-detail { width: 100vw; }
  }
`;
